import { BigNumber } from '@ethersproject/bignumber';
import { BIG_ZERO } from 'app/constants';
import { calculateLTVFromDecoratedSilo } from 'app/features/util/ltv';
import { shiftDecimal } from 'app/functions/silo/calculations';
import produce from 'immer';
import { DecoratedSilo, SiloAsset } from './types';
import { ChecksumAddress, PERCENT_DECIMALS } from '@silo/types';

export interface MutableDecoratedSilo {
  readonly siloAddress: ChecksumAddress;
  readonly assets: MutableSiloAsset[];
  readonly userLTV: BigNumber;
  readonly userLiquidationThreshold: BigNumber;
  readonly userMaximumLTV: BigNumber;
  readonly hasPosition: boolean;
}

interface MutableSiloAsset {
  readonly assetAddress: ChecksumAddress;
  totalDeposits: BigNumber;
  collateralOnlyDeposits: BigNumber;
  totalBorrowAmount: BigNumber;
  readonly liquidity: BigNumber;
  readonly utilization: BigNumber;
  readonly symbol: string;
  readonly name: string;
  readonly decimals: number;
  readonly isNative: boolean;
  readonly isPaused: boolean;
  readonly depositAPY: BigNumber;
  readonly borrowAPY: BigNumber;
  userBalance: BigNumber;
  collateralBalance: BigNumber;
  collateralOnlyBalance: BigNumber;
  debtBalance: BigNumber;
  projectedDebtBalance: BigNumber;
  readonly priceInNative: BigNumber;
}

export type Recipe = (previousDecoratedSilo: MutableDecoratedSilo) => void;

export const mutateDecoratedSilo = (decoratedSilo: DecoratedSilo, recipe: Recipe) => {
  const updatedDecoratedSilo = produce(decoratedSilo, recipe);

  return hydrateDecoratedSilo(updatedDecoratedSilo);
};

/**
 * Calculates all derived properties of a decorated silo. Used for optimistic updates.
 */
const hydrateDecoratedSilo = (decoratedSilo: DecoratedSilo): DecoratedSilo => {
  const { assets } = decoratedSilo;
  const LTV = calculateLTVFromDecoratedSilo(decoratedSilo);
  const updatedAssets = assets.map(hydrateSiloAsset);

  return {
    ...decoratedSilo,
    assets: updatedAssets,
    userLTV: LTV ? BigNumber.from(LTV.userLTV) : BIG_ZERO,
    userMaximumLTV: LTV ? BigNumber.from(LTV.userMaximumLTV) : BIG_ZERO,
    userLiquidationThreshold: LTV ? BigNumber.from(LTV.userLiquidationThreshold) : BIG_ZERO,
    hasPosition: getHasPosition(updatedAssets),
  };
};

const getHasPosition = (assets: SiloAsset[]) => assets.some(getHasAssetPosition);

const getHasAssetPosition = ({ collateralBalance, collateralOnlyBalance, debtBalance }: SiloAsset) =>
  !collateralBalance.isZero() || !collateralOnlyBalance.isZero() || !debtBalance.isZero();

const hydrateSiloAsset = (siloAsset: SiloAsset): SiloAsset => {
  const { totalDeposits, totalBorrowAmount } = siloAsset;

  const liquidity = getLiquidity(totalDeposits, totalBorrowAmount);
  const utilization = getUtilization(totalBorrowAmount, totalDeposits);

  return {
    ...siloAsset,
    liquidity,
    utilization,
  };
};

const getLiquidity = (totalDeposits: BigNumber, totalBorrowAmount: BigNumber) => totalDeposits.sub(totalBorrowAmount);

const getUtilization = (totalBorrowAmount: BigNumber, totalDeposits: BigNumber): BigNumber => {
  if (totalDeposits.isZero()) return BIG_ZERO;

  return shiftDecimal(totalBorrowAmount, PERCENT_DECIMALS).div(totalDeposits);
};
